/**
 * LIFO registry of web back handlers for the mobile WebView.
 *
 * State-based overlays (modal, drawer, bottom sheet) hide their close button and
 * mask in the mobile WebView, so the native back button (hardware/gesture on
 * Android, header back) is the only way to dismiss them. By default native back
 * bypasses web — it unwinds web history or pops the WebView. A component that
 * registers here claims the native back button for as long as it is registered:
 *
 *   - Web signals native whenever a handler becomes active (the stack goes from
 *     empty to non-empty) or inactive (back to empty), via the optional
 *     `setMobileBackHandlerActive` bridge method (absent on older mobile builds).
 *   - While a handler is active, native invokes web's topmost handler on back
 *     press (through the generic `__btReceiveRNEvent` bridge) instead of running
 *     its default back behavior.
 *
 * The stack is LIFO so nested overlays (e.g. a drawer opened over a modal) unwind
 * last-opened-first. This module mirrors the module-singleton style of
 * `rnEventBridge.ts` and the push/pop-by-identity semantics of `RootFocusObserver`.
 *
 * Consumers should use the `useMobileBackHandler` hook rather than these
 * functions directly.
 */
import { RESERVED_MOBILE_TO_WEB_EVENT_KEYS } from "@bt-mobile/shared-contracts";

import { mobile } from "./mobile";
import { subscribeRNEvent } from "./rnEventBridge";

/** `__btReceiveRNEvent` key native fires to invoke web's topmost back handler. */
export const BACK_PRESS_KEY = RESERVED_MOBILE_TO_WEB_EVENT_KEYS.backHandler;

export type BackHandler = () => void;

const stack: BackHandler[] = [];

let unsubscribeFromBackPress: (() => void) | undefined;

/**
 * Runs the topmost registered handler, if any. `subscribeRNEvent` already
 * isolates handler throws, so no extra try/catch is needed here.
 */
function handleBackPress(): void {
  stack[stack.length - 1]?.();
}

/**
 * Tells native whether a web back handler is currently active. No-ops outside the
 * WebView, and optional-chains because `setMobileBackHandlerActive` only exists on
 * newer mobile versions — older apps keep their default back behavior.
 */
function signalActive(active: boolean): void {
  if (!mobile.isWebview) return;
  mobile.setMobileBackHandlerActive?.({ active });
}

/**
 * Pushes a back handler onto the LIFO stack and returns an unregister function
 * that removes *that specific* handler (a middle overlay may unmount before the
 * one above it, so we remove by identity rather than popping).
 *
 * When the stack transitions empty → non-empty, native is signaled active and
 * the inbound back-press subscription is installed lazily. When it transitions
 * back to empty, native is signaled inactive and the subscription is torn down.
 *
 * No-ops for signaling/subscription outside the WebView, but still tracks the
 * stack so behavior is consistent.
 */
export function registerBackHandler(handler: BackHandler): () => void {
  const wasEmpty = stack.length === 0;
  stack.push(handler);

  if (wasEmpty) {
    unsubscribeFromBackPress = subscribeRNEvent(
      BACK_PRESS_KEY,
      handleBackPress,
    );
    signalActive(true);
  }

  let unregistered = false;
  return () => {
    if (unregistered) return;
    unregistered = true;

    const index = stack.lastIndexOf(handler);
    if (index !== -1) {
      stack.splice(index, 1);
    }

    if (stack.length === 0) {
      unsubscribeFromBackPress?.();
      unsubscribeFromBackPress = undefined;
      signalActive(false);
    }
  };
}
